import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import type { UpstreamApiTagResult } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

import useDashboardFilters from './useDashboardFilters';

export const TagSentimentFilter = {
  ALL: 'all',
  POSITIVE: 1,
  NEUTRAL: 0,
  NEGATIVE: -1,
};

export type SentimentFilterValues =
  (typeof TagSentimentFilter)[keyof typeof TagSentimentFilter];

const fetchTagsData = async (
  endpoint: string,
  language: string,
  filters: Record<string, string[]>,
  question: string | null,
  textFilter?: string,
  sentimentFilter?: SentimentFilterValues,
  signal?: AbortSignal,
): Promise<UpstreamApiTagResult> =>
  HttpService.post(
    endpoint,
    {
      language,
      filters,
      question: question || '',
      text_filter: textFilter || '',
      sentiment_filter:
        typeof sentimentFilter === 'string' ? null : sentimentFilter,
    },
    undefined,
    undefined,
    { signal },
  )
    .then((data) => Promise.resolve(data.data))
    .catch((error) => Promise.reject(error.response.data));

const useTagsQuery = (
  enable: boolean,
  question: string | null,
  dashboardId?: number,
  textFilter?: string,
  sentimentFilter?: SentimentFilterValues,
) => {
  const { flatFiltersDebounced } = useDashboardFilters();
  const { i18n } = useTranslation();

  const endpoint = useMemo(
    () => generatePath(apiPaths.DASHBOARD.TAGS, { id: dashboardId }),
    [dashboardId],
  );
  const flatFiltersLength = Object.keys(flatFiltersDebounced).length;

  return useQuery<UpstreamApiTagResult>({
    queryKey: [
      endpoint,
      i18n.language,
      flatFiltersDebounced,
      question,
      textFilter,
      sentimentFilter,
    ],
    queryFn: ({ signal }) =>
      fetchTagsData(
        endpoint,
        i18n.language,
        flatFiltersDebounced,
        question ?? '',
        textFilter,
        sentimentFilter,
        signal,
      ),
    enabled: enable && !!dashboardId && flatFiltersLength > 0,
  });
};

export default useTagsQuery;
